// ─── The rules behind the « Notifications » screen (2026-10-01) ─────────────
//
// Plan docs/superpowers/plans/2026-10-01-pwa-push.md, piece 4. For each alert
// type the owner picks push, WhatsApp, both, or the bell only. The worker
// (lib/pushRouting.ts) owns the list of types and which group each is in;
// this file decides how the screen shows them. No JSX here, so every rule can
// be tested on its own (notificationPrefs.test.ts).
import type { AlertGroup, Channel, PushPrefKind } from '@/lib/pushPrefsApi';

/** The four choices, in the order the segmented control shows them. */
export const CHANNELS: ReadonlyArray<{ id: Channel; label: string; hint: string }> = [
  { id: 'push', label: 'Push', hint: 'a notification on your phone or computer, once notifications are on' },
  { id: 'whatsapp', label: 'WhatsApp', hint: 'a message on your WhatsApp' },
  { id: 'both', label: 'Both', hint: 'push and WhatsApp' },
  { id: 'bell', label: 'Bell', hint: 'only the bell here in the admin — no ping' },
];

/**
 * The default choice for a group.
 *
 * ⚠️ A MIRROR of worker lib/pushRouting.ts `defaultChannel` — change both
 * together. GET /push/prefs says whether a row is AT its default
 * (`isDefault`), not which choice the default is, and the screen needs that to
 * put « default » on the right option and to reset a group.
 */
export function defaultChannel(group: AlertGroup): Channel {
  switch (group) {
    case 'reply': return 'whatsapp';
    case 'info': return 'bell';
    default: return 'push'; // now, system
  }
}

/**
 * Why a choice cannot be picked, or null when it can.
 *
 * ⚠️ A WHATSAPP-DOWN ALERT CANNOT TRAVEL OVER WHATSAPP. The system group is
 * the line going down and the relay going silent: « WhatsApp » alone would
 * send the warning through the very thing that is broken. « Both » stays
 * open — the push half still arrives.
 */
export function lockReason(group: AlertGroup, channel: Channel): string | null {
  if (group === 'system' && channel === 'whatsapp') {
    return 'WhatsApp alone is off here: a WhatsApp-down alert can’t travel over WhatsApp.';
  }
  return null;
}

export interface PrefGroup {
  id: AlertGroup;
  title: string;
  /** One line: what this group is for. */
  explanation: string;
}

/** ⚠️ THE ORDER IS THE POINT: top-down by how much each group needs the owner. */
export const GROUPS: ReadonlyArray<PrefGroup> = [
  {
    id: 'reply',
    title: 'You reply to these on WhatsApp',
    explanation: 'The agent is waiting for your answer — reply on WhatsApp and it learns from you.',
  },
  {
    id: 'now',
    title: 'Needs you now',
    explanation: 'Something failed or a customer is waiting — act soon.',
  },
  {
    id: 'system',
    title: 'System — WhatsApp down, server silent',
    explanation: 'The WhatsApp line or the server has stopped working.',
  },
  {
    id: 'info',
    title: 'Everything else — bell only',
    explanation: 'Good to know, nothing urgent — by default it waits in the bell, without a ping.',
  },
];

const KNOWN = new Set<string>(GROUPS.map((g) => g.id));

/**
 * The four groups in order, each with its alert types in the worker's order.
 * Empty groups stay, so the screen always has the same four headings.
 *
 * ⚠️ NEVER DROPPED: a group this screen does not know lands in « now », the
 * same rule the worker uses for a kind it does not know (pushRouting.groupOf).
 */
export function groupKinds(kinds: ReadonlyArray<PushPrefKind>): Array<PrefGroup & { kinds: PushPrefKind[] }> {
  const groupOf = (k: PushPrefKind): AlertGroup => (KNOWN.has(k.group) ? k.group : 'now');
  return GROUPS.map((g) => ({ ...g, kinds: kinds.filter((k) => groupOf(k) === g.id) }));
}

// ─── Optimistic saves ────────────────────────────────────────────────────────
//
// A tap shows at once; the save runs behind it. Only rows the owner touched
// have an entry here — an untouched row shows what the worker sent.

export interface RowEdit {
  /** What the control shows: the newest choice, saved or not. */
  shown: Channel;
  /** What the worker last accepted — where a failed save goes back to. */
  confirmed: Channel;
  saving: boolean;
  /** Why the last save failed. Cleared by the next choice. */
  error: string | null;
}
export type Edits = Readonly<Record<string, RowEdit>>;

export type EditAction =
  /** `from` is the worker's value, used the first time a row is touched. */
  | { type: 'choose'; kind: string; channel: Channel; from: Channel }
  /** `latest`: no newer choice was made on this row since this save started. */
  | { type: 'saved'; kind: string; channel: Channel; latest: boolean }
  | { type: 'failed'; kind: string; error: string; latest: boolean };

/**
 * ⚠️ THE NEWEST TAP DECIDES. Two quick taps start two saves. An older save
 * that fails must not undo the newer tap, and when the newest fails the row
 * goes back to what the worker really holds — which may be the older tap, if
 * that one was accepted — not to whatever it showed before both.
 */
export function editsReducer(edits: Edits, a: EditAction): Edits {
  const cur = edits[a.kind];
  switch (a.type) {
    case 'choose':
      return { ...edits, [a.kind]: { shown: a.channel, confirmed: cur?.confirmed ?? a.from, saving: true, error: null } };
    case 'saved':
      if (!cur) return edits;
      return { ...edits, [a.kind]: { ...cur, confirmed: a.channel, saving: a.latest ? false : cur.saving } };
    case 'failed':
      if (!cur || !a.latest) return edits;
      return { ...edits, [a.kind]: { ...cur, shown: cur.confirmed, saving: false, error: a.error } };
  }
}

/** The choice a row shows right now. */
export function shownChannel(edits: Edits, k: Pick<PushPrefKind, 'kind' | 'channel'>): Channel {
  return edits[k.kind]?.shown ?? k.channel;
}
